
import { Guid } from "guid-typescript";
import moment from "moment"
import Passenger from '@/components/Passenger/models/Passenger';
import Helpers from '@/util/Helpers';

export enum ArrivalDepartureType {
    Unknown = 0,
    Arrival = 1,
    Departure = 2
}

export class ArrivalDeparture {
    id: Guid;
    tripId: Guid;
    movitId: number;
    movitInstanceId: number;
    placeId: number;
    passengerId: number;
    passengerName: string;
    transportName: string;
    at: Date; 
    atOther: Date | null; 
    arrivalDepartureType: ArrivalDepartureType;
    cancelled: boolean;
    cancelledByEmail: string;
    cancelling: boolean;
    cancellationReason: string;
    completed: boolean;

    constructor(options: Partial<ArrivalDeparture>) {
        this.id = options ? options.id : Guid.create();
        this.tripId = options ? options.tripId : null;
        this.movitId = options ? options.movitId : 0;
        this.movitInstanceId = options ? options.movitInstanceId : 0;
        this.at = options ? new Date(options.at) : null;
        this.atOther = options && options.atOther ? new Date(options.atOther) : null;
        this.passengerId = options ? options.passengerId : 0;
        this.passengerName = options ? options.passengerName : '';
        this.placeId = options ? options.placeId : null;
        this.transportName = options ? options.transportName : null;
        this.arrivalDepartureType = options ? options.arrivalDepartureType : ArrivalDepartureType.Unknown;
        this.cancelled = options ? options.cancelled : false;
        this.cancelledByEmail = options ? options.cancelledByEmail : null;
        this.cancelling = options ? options.cancelling : false;
        this.cancellationReason = options ? options.cancellationReason : "";
        this.completed = options ? options.completed : false;
    }

    get isValid() {
       return this.at 
       && this.at.getTime() > 0
       && this.placeId > 0
       && this.passengerId > 0;
    }

    get atHHMM(){
        return (this.at.getHours()).toString().padStart(2, "0") 
        + ":" 
        + (this.at.getMinutes()).toString().padStart(2, '0');
    }

    get atYMD(){
        return this.at.getFullYear() 
        + "-" 
        + (this.at.getMonth() + 1).toString().padStart(2, "0") 
        + "-" 
        + (this.at.getDate()).toString().padStart(2, '0');
    }

    get atOtherHHMM(): string{
        return this.atOther 
            ? (this.atOther.getHours()).toString().padStart(2, "0") 
            + ":" 
            + (this.atOther.getMinutes()).toString().padStart(2, '0')
            : "unknown";
    }

    get atOtherYMD(): string {
        return this.atOther 
            ? this.atOther.getFullYear() 
            + "-" 
            + (this.atOther.getMonth() + 1).toString().padStart(2, "0") 
            + "-" 
            + (this.atOther.getDate()).toString().padStart(2, '0')
            : "unknown";
    }

    get hasPassed(): boolean {
        const nowMoment = moment();
        const atMoment = moment(this.at);
        return nowMoment.isAfter(atMoment.endOf('day')); 
    }

    get isArrival(){
        return this.arrivalDepartureType === ArrivalDepartureType.Arrival;
    }

    get isDeparture(){
        return this.arrivalDepartureType === ArrivalDepartureType.Departure;
    }

    get cancelledByName(){
        return Helpers.nameFromEmail(this.cancelledByEmail);
    }

    public static fromApi(apiArrivalDeparture: any): ArrivalDeparture {
        const arrivalDeparture = new ArrivalDeparture(apiArrivalDeparture);
        return arrivalDeparture;
    }

    public static toApi(model: ArrivalDeparture): any {
        const apiModel = {
            id: model.id,
            tripId: model.tripId,
            movitId: model.movitId,
            movitInstanceId: model.movitInstanceId,
            placeId: model.placeId,
            passengerId: model.passengerId,
            passengerName: model.passengerName,
            transportName: model.transportName,
            at: model.at,
            atOther: model.atOther, 
            arrivalDepartureType: model.arrivalDepartureType,
            cancelled: model.cancelled,
            cancelling: model.cancelling,
            cancellationReason: model.cancellationReason,
            completed: model.completed
        }
        return apiModel;
    }

    public static sortByDate(arrivalsDepartures: ArrivalDeparture[]): ArrivalDeparture[] {
        return arrivalsDepartures.sort((a, b) => {
            if (a.at.getTime() < b.at.getTime()) {
                return -1;
            }
            if (a.at.getTime() > b.at.getTime()) {
                return 1;
            }
            return 0;
        })
    }

}

export default ArrivalDeparture;
